import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles, Brain, Activity, Heart } from 'lucide-react';
import { useDemoMode } from '../context/DemoContext';

export function LandingPage() {
  const navigate = useNavigate();
  const { startDemo } = useDemoMode();

  const mainFeatures = [
    {
      title: "Smart Recovery",
      description: "AI-powered recovery tracking and optimization",
      icon: <Heart className="w-5 h-5 text-primary" />,
      color: 'from-primary/20 to-purple-500/20'
    },
    {
      title: "Elite Training",
      description: "Personalized workouts based on your readiness",
      icon: <Activity className="w-5 h-5 text-success" />,
      color: 'from-success/20 to-green-500/20'
    },
    {
      title: "Neural Insights",
      description: "Advanced analytics for peak performance",
      icon: <Brain className="w-5 h-5 text-blue-500" />,
      color: 'from-blue-500/20 to-cyan-500/20'
    }
  ];

  const handleGetStarted = () => {
    navigate('/welcome');
  };

  const handleTryDemo = () => {
    startDemo();
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Hero Section */}
      <div className="relative min-h-screen flex flex-col justify-center px-4 py-12 sm:py-16">
        {/* Ambient Background */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/20 via-purple-500/10 to-background"></div>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,_var(--tw-gradient-stops))] from-blue-500/10 via-cyan-500/5 to-transparent"></div>
          <div className="absolute inset-0 backdrop-blur-[100px]"></div>
        </div>

        <div className="relative container mx-auto max-w-7xl">
          {/* Version Tag */}
          <div className="text-center mb-8">
            <span className="inline-block px-3 py-1 text-xs sm:text-sm text-primary bg-primary/10 rounded-full">
              Version 3.5.7 (build pvl)
            </span>
          </div>

          {/* Hero Content */}
          <div className="text-center space-y-6 mb-12">
            <div className="flex items-center justify-center gap-2 text-primary">
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-xs sm:text-sm font-medium">Welcome to the Future of Fitness</span>
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold bg-gradient-to-r from-primary via-purple-400 to-blue-500 bg-clip-text text-transparent">
              TODAY Move
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto">
              Experience the next evolution in fitness technology, powered by advanced AI and personalized analytics.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleGetStarted}
                className="w-full sm:w-auto theme-button px-6 py-3 rounded-lg flex items-center justify-center gap-2"
              >
                <span>Get Started</span>
                <ArrowRight className="w-4 h-4" />
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleTryDemo}
                className="w-full sm:w-auto bg-primary/20 text-primary hover:bg-primary/30 px-6 py-3 rounded-lg flex items-center justify-center gap-2 transition-colors"
              >
                <Sparkles className="w-4 h-4" />
                <span>Try Demo</span>
              </motion.button>
            </div>
          </div>

          {/* Main Features */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {mainFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="theme-card p-4 sm:p-6 relative overflow-hidden"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${feature.color}`} />
                <div className="relative space-y-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    {feature.icon}
                  </div>
                  <h3 className="font-semibold">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}